import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet-async";

import SEO from "../../components/SEO";
import { getHomepageSchema, getBreadcrumbSchema, getWebPageSchema, getFAQPageSchema, combineSchemas } from "../../utils/structuredData";
import { HOME_FAQS } from "../../data/homeFaqs";
// Public Components
import Header from "../../components/Header";
import HeroSection from "../../components/HeroSection";
import UpcomingEventBanner from "../../components/UpcomingEventBanner";
import ProgramsSection from "../../components/ProgramsSection";
import WhyOwazaSection from "../../components/WhyOwazaSection";
import StepUpAffiliationSection from "../../components/StepUpAffiliationSection";
import FaqSection from "../../components/FaqSection";
import ReviewsSection from "../../components/ReviewsSection";
import ExpectationSection from "../../components/ExpectationSection";
import LocationSection from "../../components/LocationSection";
import Footer from "../../components/Footer";
import AnnouncementRibbon from "../../components/AnnouncementRibbon";
import LineagePage from "../../components/LineagePage";
import GalleryPage from "../../components/GalleryPage";
import CustomPage from "../../components/CustomPage";
import ProgramPage from "../../components/ProgramPage";
// Newsletter
import { NewsletterArchive, NewsletterView } from "../../components/NewsletterArchive";
// QR Code Landing Pages
import {
  ReviewRedirect,
  FeedbackForm,
  PhotoUpload,
  EventsPage,
  EventDetailPage,
  ShareExperience,
} from "../../components/public";
// Member Registration
import MemberRegistrationForm from "../../components/public/MemberRegistrationForm";
import EmailVerificationPending from "../../components/public/EmailVerificationPending";
import EmailVerificationPage from "../../components/public/EmailVerificationPage";
import TemporaryPreviewPage from "../../components/public/TemporaryPreviewPage";

const Home = () => {
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace('#', '');
        const attemptScroll = (attempt = 0) => {
          const element = document.getElementById(id);
          if (element) {
            const headerOffset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          } else if (attempt < 5) {
            setTimeout(() => attemptScroll(attempt + 1), 100 * (attempt + 1));
          }
        };
        setTimeout(() => attemptScroll(), 100);
      }
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Martial Arts Classes in Spring Hill, FL"
        description="Expert Jiu-Jitsu, Karate, Judo, Tai Chi & Aikido for kids and adults in Spring Hill, FL. Appointment-only. Call to schedule your first class."
        canonical="/"
        jsonLd={combineSchemas(
          getHomepageSchema(),
          getFAQPageSchema({
            title: "Owaza Martial Arts — Frequently Asked Questions",
            url: "/#faq",
            faqs: HOME_FAQS,
          })
        )}
      />
      <AnnouncementRibbon />
      <Header />
      <main id="main-content">
        <HeroSection />
        <UpcomingEventBanner />
        <StepUpAffiliationSection />
        <ProgramsSection />
        <WhyOwazaSection />
        <FaqSection />
        <ReviewsSection />
        <ExpectationSection />
        <LocationSection />
      </main>
      <Footer />
    </div>
  );
};

const LineageWrapper = () => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Lineage", path: "/lineage" }
  ]);
  const pageSchema = getWebPageSchema({
    title: "Our Martial Arts Lineage & History",
    description: "Explore the rich heritage and lineage of Owaza Ryu Ju-Jitsu, tracing our martial arts tradition and instructors.",
    url: "/lineage"
  });

  return (
    <div className="min-h-screen">
      <SEO
        title="Our Martial Arts Lineage & History"
        description="Explore the lineage of Owaza Ryu Ju-Jitsu — masters and heritage rooted in Spring Hill, FL. Discover the instructors behind our curriculum."
        canonical="/lineage"
        jsonLd={combineSchemas(breadcrumbs, pageSchema)}
      />
      <Header />
      <main id="main-content">
        <LineagePage />
      </main>
      <Footer />
    </div>
  );
};

const GalleryWrapper = () => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Gallery", path: "/gallery" }
  ]);
  const pageSchema = getWebPageSchema({
    title: "Photo & Video Gallery",
    description: "View photos and videos from Owaza Martial Arts training sessions, events, and belt promotions in Spring Hill, FL.",
    url: "/gallery"
  });

  return (
    <div className="min-h-screen">
      <SEO
        title="Photo & Video Gallery"
        description="Photos and videos from Owaza Martial Arts in Spring Hill, FL — training sessions, events, and belt promotions. See what real martial arts looks like."
        canonical="/gallery"
        jsonLd={combineSchemas(breadcrumbs, pageSchema)}
      />
      <Header />
      <main id="main-content">
        <GalleryPage />
      </main>
      <Footer />
    </div>
  );
};

const CustomPageWrapper = () => (
  <div className="min-h-screen">
    <Header />
    <main id="main-content">
      <CustomPage />
    </main>
    <Footer />
  </div>
);

const ProgramPageWrapper = () => (
  <div className="min-h-screen">
    <Header />
    <main id="main-content">
      <ProgramPage />
    </main>
    <Footer />
  </div>
);

const NotFound = () => (
  <div className="min-h-screen">
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
      <title>Page Not Found - Owaza Martial Arts</title>
    </Helmet>
    <Header />
    <main id="main-content" className="flex flex-col items-center justify-center py-32 px-4">
      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-xl text-gray-600 mb-8">This page does not exist.</p>
      <a href="/" className="text-blue-600 hover:text-blue-800 underline text-lg">
        Return to Owaza Martial Arts
      </a>
    </main>
    <Footer />
  </div>
);

const EventsWrapper = ({ children }) => (
  <div className="min-h-screen">
    <Header />
    <main id="main-content">{children}</main>
    <Footer />
  </div>
);

const NewsletterArchiveWrapper = () => (
  <div className="min-h-screen">
    <Header />
    <main id="main-content">
      <NewsletterArchive />
    </main>
    <Footer />
  </div>
);

const NewsletterViewWrapper = () => (
  <div className="min-h-screen">
    <Header />
    <main id="main-content">
      <NewsletterView />
    </main>
    <Footer />
  </div>
);

function PublicApp() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/lineage" element={<LineageWrapper />} />
            <Route path="/gallery" element={<GalleryWrapper />} />
            <Route path="/page/:slug" element={<CustomPageWrapper />} />
            <Route path="/programs/:slug" element={<ProgramPageWrapper />} />
            {/* Newsletter Archive Routes */}
            <Route path="/newsletter/:token" element={<NewsletterArchiveWrapper />} />
            <Route path="/newsletter/:token/:id" element={<NewsletterViewWrapper />} />
            {/* QR Code Landing Pages */}
            <Route path="/review" element={<ReviewRedirect />} />
            <Route path="/feedback" element={<FeedbackForm />} />
            <Route path="/upload" element={<PhotoUpload />} />
            <Route path="/events" element={<EventsWrapper><EventsPage /></EventsWrapper>} />
            <Route path="/events/:eventId" element={<EventsWrapper><EventDetailPage /></EventsWrapper>} />
            <Route path="/share" element={<ShareExperience />} />
            {/* Member Registration */}
            <Route path="/register" element={<MemberRegistrationForm />} />
            <Route path="/register/preview" element={<TemporaryPreviewPage />} />
            <Route path="/register/verify-pending" element={<EmailVerificationPending />} />
            <Route path="/register/verify" element={<EmailVerificationPage />} />
            {/* Catch-all 404 for unknown routes */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default PublicApp;
